import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, Sparkles, Tag, ToggleLeft, Wand2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SettingsToggleRow } from '@/components/settings/SettingsToggleRow';
import { useAuth } from '@/auth/AuthContext';
import { useExtensionUtil, useHasExtension, type ExtensionUtilContextType } from '@/hooks/useExtensionUtil';
import { useSessionsStore } from '@/stores/sessions-store';
import { isExtension } from '@/utils/platform';
import {
  DEFAULT_SESSION_CONTEXT_GLOBAL_CONFIG,
  type SessionContextGlobalConfig,
} from '@/types/session-context';

interface SessionContextSettingsProps {
  /** Omit page title when embedded in Profile → Browsing tab. */
  embedded?: boolean;
}

function isEmbeddedExtensionRuntime(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.location.protocol === 'chrome-extension:' ||
    window.troveApp?.isExtension?.() === true ||
    isExtension()
  );
}

function mergeSessionContextConfig(
  raw: Partial<SessionContextGlobalConfig> | undefined
): SessionContextGlobalConfig {
  return { ...DEFAULT_SESSION_CONTEXT_GLOBAL_CONFIG, ...raw };
}

/** React Strict Mode mounts twice in dev — coalesce duplicate config fetches. */
const CONFIG_GET_DEDUPE_MS = 500;
let lastConfigGetAt = 0;

function requestSessionContextConfig(
  sendMessage: ExtensionUtilContextType['sendMessage']
): void {
  const now = Date.now();
  if (now - lastConfigGetAt < CONFIG_GET_DEDUPE_MS) return;
  lastConfigGetAt = now;
  sendMessage('tabMgm', { action: 'sessionContextConfig', mode: 'get' });
}

export function SessionContextSettings({ embedded = false }: SessionContextSettingsProps) {
  const { t } = useTranslation('common');
  const { user, updatePreference } = useAuth();
  const { sendMessage } = useExtensionUtil();
  const extensionDetected = useHasExtension();
  const embeddedExtension = isEmbeddedExtensionRuntime();
  const extensionConnected = embeddedExtension || extensionDetected === true;
  const showExtensionRequired = !embeddedExtension && extensionDetected === false;
  const controlsDisabled = !extensionConnected;

  const { sessionUpdate } = useSessionsStore();
  const serverConfig = user?.preference?.sessionContextGlobal;
  const hasServerConfig = serverConfig != null;
  const [config, setConfig] = useState<SessionContextGlobalConfig>(
    DEFAULT_SESSION_CONTEXT_GLOBAL_CONFIG
  );
  const pendingConfigRef = useRef<string | null>(null);
  const pushedServerConfigRef = useRef<string | null>(null);
  const configRef = useRef(config);

  useEffect(() => {
    configRef.current = config;
  }, [config]);

  /** Firebase preference is the source of truth for this settings UI. */
  useEffect(() => {
    if (!serverConfig) return;
    const merged = mergeSessionContextConfig(serverConfig);
    setConfig(merged);
    configRef.current = merged;
  }, [serverConfig]);

  /** Keep extension runtime aligned with server preference when present. */
  useEffect(() => {
    if (!extensionConnected || !serverConfig) return;
    const key = JSON.stringify(serverConfig);
    if (pushedServerConfigRef.current === key) return;
    pushedServerConfigRef.current = key;
    sendMessage('tabMgm', {
      action: 'sessionContextConfig',
      mode: 'set',
      config: mergeSessionContextConfig(serverConfig),
    });
  }, [extensionConnected, sendMessage, serverConfig]);

  /** Fall back to extension-local config only before any server preference exists. */
  useEffect(() => {
    if (!extensionConnected || hasServerConfig) return;
    requestSessionContextConfig(sendMessage);
  }, [extensionConnected, hasServerConfig, sendMessage]);

  useEffect(() => {
    if (hasServerConfig) return;

    const update = sessionUpdate as Record<string, unknown> | undefined;
    if (update?.type !== 'sessionContextConfig' || !update.config) return;

    const incoming = mergeSessionContextConfig(
      update.config as Partial<SessionContextGlobalConfig>
    );
    const incomingKey = JSON.stringify(incoming);

    if (pendingConfigRef.current !== null) {
      if (incomingKey === pendingConfigRef.current) {
        pendingConfigRef.current = null;
        setConfig(incoming);
        configRef.current = incoming;
      }
      return;
    }

    setConfig(incoming);
    configRef.current = incoming;
  }, [hasServerConfig, sessionUpdate]);

  const patch = useCallback(
    (next: Partial<SessionContextGlobalConfig>) => {
      if (controlsDisabled) return;
      const merged = mergeSessionContextConfig({ ...configRef.current, ...next });
      pendingConfigRef.current = JSON.stringify(merged);
      setConfig(merged);
      configRef.current = merged;
      sendMessage('tabMgm', {
        action: 'sessionContextConfig',
        mode: 'set',
        config: merged,
      });
      if (user) {
        void updatePreference({ sessionContextGlobal: merged })
          .then(() => {
            pendingConfigRef.current = null;
          })
          .catch((err) => {
            pendingConfigRef.current = null;
            console.error('Failed to sync session context preference:', err);
          });
      }
    },
    [controlsDisabled, sendMessage, updatePreference, user]
  );

  const subControlsDisabled = controlsDisabled || !config.enabled;

  return (
    <div id="session-context" className={embedded ? 'space-y-4' : 'max-w-xl space-y-6'}>
      {!embedded ? (
        <div>
          <h2 className="text-lg font-semibold">{t('sessions.contextGuard.settingsTitle')}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('sessions.contextGuard.settingsDescription')}
          </p>
        </div>
      ) : null}

      {showExtensionRequired ? (
        <p className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
          {t('sessions.contextGuard.extensionRequired')}
        </p>
      ) : null}

      <div className="divide-y divide-border/60 rounded-xl border border-border">
        <div className="px-3">
          <SettingsToggleRow
            icon={Wand2}
            title={t('sessions.contextGuard.enabled')}
            description={t('sessions.contextGuard.enabledDesc')}
            checked={config.enabled}
            disabled={controlsDisabled}
            ariaLabel={t('sessions.contextGuard.enabled')}
            onChange={() => patch({ enabled: !config.enabled })}
          />
        </div>
        <div className="px-3">
          <SettingsToggleRow
            icon={Tag}
            title={t('sessions.contextGuard.showBadge')}
            description={t('sessions.contextGuard.showBadgeDesc')}
            checked={config.showStrayTabBadge}
            disabled={subControlsDisabled}
            ariaLabel={t('sessions.contextGuard.showBadge')}
            onChange={() => patch({ showStrayTabBadge: !config.showStrayTabBadge })}
          />
        </div>
        <div className="px-3">
          <SettingsToggleRow
            icon={ToggleLeft}
            title={t('sessions.contextGuard.suggestRuleLearning')}
            description={t('sessions.contextGuard.suggestRuleLearningDesc')}
            checked={config.suggestRuleLearning}
            disabled={subControlsDisabled}
            ariaLabel={t('sessions.contextGuard.suggestRuleLearning')}
            onChange={() => patch({ suggestRuleLearning: !config.suggestRuleLearning })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <p className="text-sm font-semibold text-foreground">
            {t('sessions.contextGuard.automation')}
          </p>
          <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
            {t('sessions.contextGuard.automationDesc')}
          </p>
        </div>
        <div className="relative">
          <select
            className="w-full cursor-pointer appearance-none rounded-xl border border-border bg-muted/20 px-3 py-2.5 pr-9 text-sm font-medium text-foreground transition-all focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/10 disabled:cursor-not-allowed disabled:opacity-50"
            value={config.automationMode}
            disabled={subControlsDisabled}
            onChange={(e) =>
              patch({
                automationMode: e.target.value as SessionContextGlobalConfig['automationMode'],
              })
            }
          >
            <option value="manual">{t('sessions.contextGuard.automationManual')}</option>
            <option value="prompt_on_switch">
              {t('sessions.contextGuard.automationPromptOnSwitch')}
            </option>
          </select>
          <ChevronDown
            size={14}
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
        </div>
      </div>

      <div className="rounded-xl border border-border px-3">
        <SettingsToggleRow
          icon={Sparkles}
          title={t('sessions.contextGuard.useAiAssist')}
          description={t('sessions.contextGuard.useAiAssistDesc')}
          checked={config.useAiAssist}
          disabled={subControlsDisabled}
          ariaLabel={t('sessions.contextGuard.useAiAssist')}
          onChange={() => patch({ useAiAssist: !config.useAiAssist })}
        />
      </div>

      {!config.enabled && extensionConnected ? (
        <p className="text-xs text-muted-foreground">{t('sessions.contextGuard.enableHint')}</p>
      ) : null}
    </div>
  );
}
